import { useState } from 'react';
import { nfcLogger } from './NFCLogger';
import { useI18n } from '@/lib/i18n/client';

export interface UseNFCSupportReturn {
    nfcSupported: boolean; // derived: status === 'ready'
    browserCompatible: boolean; // derived from status mapping
    compatibilityMessage: string; // derived message ('' when ready)
    checkSupport: () => boolean;
}

// Discriminated union representing NFC support evaluation
type NFCSupportStatus =
    | { kind: 'unknown' }
    | { kind: 'insecure'; message: string }
    | { kind: 'no_ndef'; message: string; browserCompatible: boolean }
    | { kind: 'not_android'; message: string }
    | { kind: 'ready' };

export const useNFCSupport = (): UseNFCSupportReturn => {
    const { t } = useI18n();
    const [status, setStatus] = useState<NFCSupportStatus>({ kind: 'unknown' });

    function checkSupport(): boolean {
        const userAgent = navigator.userAgent;
        nfcLogger.logOperation('nfc_support_check', { userAgent });

        // Secure context check
        if (!window.isSecureContext) {
            const error = new Error(t('nfc.errors.secureContext'));
            nfcLogger.logError(error, 'secure_context_check');
            setStatus({ kind: 'insecure', message: t('nfc.errors.secureContextUi') });
            return false;
        }

        // NDEFReader feature presence
        if (!('NDEFReader' in window)) {
            const error = new Error('NDEFReader not available');
            nfcLogger.logError(error, 'ndefreader_availability_check');

            // Basic UA-based guidance (retain logic for now)
            let derivedMessage = t('nfc.errors.notSupportedBrowser');
            let browserCompatible = false;
            if (userAgent.includes('Chrome')) {
                if (userAgent.includes('Android')) {
                    derivedMessage = t('nfc.errors.chromeAndroidAvailable');
                    browserCompatible = true;
                } else {
                    derivedMessage = t('nfc.errors.chromeAndroidOnly');
                }
            } else if (userAgent.includes('Safari')) {
                derivedMessage = t('nfc.errors.safariNotSupported');
            } else if (userAgent.includes('Firefox')) {
                derivedMessage = t('nfc.errors.firefoxNotSupported');
            } else if (userAgent.includes('Edge')) {
                derivedMessage = t('nfc.errors.edgeNotSupported');
            } else {
                derivedMessage = t('nfc.errors.switchSupported');
            }

            setStatus({ kind: 'no_ndef', message: derivedMessage, browserCompatible });
            return false;
        }

        nfcLogger.logSuccess('nfc_support_check', { supported: true, browser: userAgent });

        setStatus({ kind: 'ready' });
        return true;
    }

    // Derive legacy outward-facing fields
    const nfcSupported = status.kind === 'ready';
    const browserCompatible = status.kind === 'ready'
        ? true
        : status.kind === 'no_ndef'
            ? status.browserCompatible
            : status.kind === 'unknown';
    const compatibilityMessage = status.kind === 'ready' ? '' : (
        status.kind === 'unknown' ? '' : status.message
    );

    return { nfcSupported, browserCompatible: !!browserCompatible, compatibilityMessage, checkSupport };
};
